<template>
  <v-card flat>
    <v-card-text class="pa-0">
      <div class="py-3 mx-4">
        <!-- Summary Section -->
        <v-row v-if="annualLeaveSummary" class="mb-4">
          <v-col cols="12" md="4">
            <v-card outlined class="pa-3 text-center">
              <div class="text-subtitle-2 grey--text">總計特休</div>
              <div class="text-h5 primary--text font-weight-bold">
                {{ formatLeaveDuration(annualLeaveSummary.total_minutes) }} 天
                <span class="text-subtitle-2 ml-2">
                  ({{ formatLeaveHours(annualLeaveSummary.total_minutes) }} 小時)
                </span>
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card outlined class="pa-3 text-center">
              <div class="text-subtitle-2 grey--text">已使用</div>
              <div class="text-h5 orange--text font-weight-bold">
                {{ formatLeaveDuration(annualLeaveSummary.used_minutes) }} 天
                <span class="text-subtitle-2 ml-2">
                  ({{ formatLeaveHours(annualLeaveSummary.used_minutes) }} 小時)
                </span>
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card outlined class="pa-3 text-center">
              <div class="text-subtitle-2 grey--text">剩餘</div>
              <div class="text-h5 success--text font-weight-bold">
                {{ formatLeaveDuration(annualLeaveSummary.remaining_minutes) }}
                天
                <span class="text-subtitle-2 ml-2">
                  ({{ formatLeaveHours(annualLeaveSummary.remaining_minutes) }} 小時)
                </span>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-skeleton-loader
          v-if="loading"
          type="table"
          class="mb-4"
        ></v-skeleton-loader>

        <div v-else-if="!paginatedRecords.length" class="text-center py-8">
          <v-icon size="64" color="grey lighten-1">mdi-calendar-blank</v-icon>
          <div class="text-h6 text-h4 mt-2">沒有特休假紀錄</div>
        </div>

        <template v-else>
          <v-data-table
            :headers="headers"
            :items="paginatedRecords"
            :loading="loading"
            :server-items-length="totalItems"
            :items-per-page="itemsPerPage"
            hide-default-footer
            disable-pagination
            class="elevation-1"
          >
            <template v-slot:item.start_time="{ item }">
              <formatted-date :date="item.start_time" />
            </template>

            <template v-slot:item.end_time="{ item }">
              <formatted-date :date="item.end_time" />
            </template>

            <template v-slot:item.leave_minutes="{ item }">
              {{ formatLeaveDuration(item.leave_minutes) }} 天
            </template>

            <template v-slot:item.leave_hours="{ item }">
              {{ formatLeaveHours(item.leave_minutes) }} 小時
            </template>

            <template v-slot:item.approval_status="{ item }">
              {{ item.approval_status }}
            </template>
          </v-data-table>

          <div class="text-center my-4" v-if="totalPages > 1">
            <v-pagination
              v-model="currentPage"
              :length="totalPages"
              @input="handlePageChange"
              :total-visible="7"
              color="primary"
              circle
            ></v-pagination>
          </div>
        </template>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import FormattedDate from "@/components/FormattedDate.vue";

export default {
  name: "AnnualLeavePaginationWidget",
  components: {
    FormattedDate,
  },
  props: {
    targetUserId: {
      type: [Number, String],
      required: true,
    },
    selectedCompany: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      loading: false,
      paginatedRecords: [],
      annualLeaveSummary: null,
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0,
      totalPages: 0,
      headers: [
        { text: "開始時間", value: "start_time", sortable: false },
        { text: "結束時間", value: "end_time", sortable: false },
        { text: "請假天數", value: "leave_minutes", sortable: false },
        { text: "請假小時", value: "leave_hours", sortable: false },
        { text: "事由", value: "reason", sortable: false },
        { text: "狀態", value: "approval_status", sortable: false },
      ],
    };
  },
  watch: {
    targetUserId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchAnnualLeaves();
        }
      },
    },
  },
  methods: {
    async fetchAnnualLeaves() {
      if (!this.targetUserId) return;

      this.loading = true;
      try {
        const response = await this.$axios.get(
          `/api/annual-leaves/user/${this.targetUserId}`,
          {
            params: {
              page: this.currentPage,
              limit: this.itemsPerPage,
            },
          }
        );

        const { data, annualLeave, pagination } = response.data;
        this.paginatedRecords = data || [];
        this.annualLeaveSummary = annualLeave || null;
        console.log("annualLeaveSummary", this.annualLeaveSummary);

        if (pagination) {
          this.currentPage = pagination.currentPage;
          this.itemsPerPage = pagination.pageSize;
          this.totalItems = pagination.totalItems;
          this.totalPages = pagination.totalPages;
        }
      } catch (error) {
        console.error("Error fetching annual leaves:", error);
        this.$emit("error", "無法載入特休假紀錄");
      } finally {
        this.loading = false;
      }
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.fetchAnnualLeaves();
    },
    formatLeaveDuration(minutes) {
      if (!minutes && minutes !== 0) return "-";
      const days = minutes / 480;
      // If it's a whole number, return it as format string without decimals, else 2 decimals
      return Number.isInteger(days) ? days.toString() : days.toFixed(2);
    },
    formatLeaveHours(minutes) {
      if (!minutes && minutes !== 0) return "-";
      const hours = minutes / 60;
      return Number.isInteger(hours) ? hours.toString() : hours.toFixed(2);
    },
    getStatusColor(status) {
      if (status === "審核通過") return "success";
      if (status === "待審核") return "warning";
      if (status === "審核拒絕") return "error";
      return "grey";
    },
  },
};
</script>

<style scoped>
.v-data-table {
  background: transparent !important;
}
</style>
